import { useContext, useEffect } from "react";

import { COMPONENTS } from "@/constants/components";
import { ExperimentIds } from "@/constants/experiments";
import { FitnessGoals } from "@/constants/fitnessGoal";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import usePaymentSystem from "@/hooks/usePaymentSystem";
import { useEnableExperiment } from "@/utils/ExperimentsProvider";
import { useGender } from "@/utils/GenderProvider";
import { useCurrency } from "@/utils/GeolocationProvider";
import { FlowProviderContext } from "@/utils/providers/FlowProvider";
import { useStrapiExperimentsIds } from "@/utils/providers/StrapiQuestionsProvider";
import { FlowTypes } from "@/utils/providers/types";
import useAnswers from "@/utils/useAnswers";

import { useDeviceType } from "./useDeviceType";

export const useEnableExps = ({
    initComponent,
    currentComponent,
}: {
    isFlowType: Partial<Record<FlowTypes, boolean>>;
    initComponent: COMPONENTS | null;
    nextComponent?: COMPONENTS | null;
    currentComponent?: COMPONENTS | null;
}) => {
    const { isFemale } = useGender();
    const { isLoaded: isCurrencyLoaded } = useCurrency();
    const { getAnswersValue } = useAnswers();
    const { flowType } = useContext(FlowProviderContext);
    const { enableExperiment } = useEnableExperiment();
    const { isAndroid } = useDeviceType();
    const strapiExperimentsIds = useStrapiExperimentsIds();
    const { isApplePayAvailable } = usePaymentSystem();

    const primaryGoal = getAnswersValue(QUESTIONS_IDS.PRIMARY_GOAL);
    const eventMotivation = getAnswersValue(QUESTIONS_IDS.EVENT_MOTIVATION);
    const supportBoostsMotivation = getAnswersValue(
        QUESTIONS_IDS.STATEMENT_SUPPORT_BOOSTS_MOTIVATION
    );
    const isLoseWeightGoal = primaryGoal === FitnessGoals.LoseWeight;
    const waterConsumption = getAnswersValue(QUESTIONS_IDS.WATER_CONSUMPTION);
    const importantElements = getAnswersValue(QUESTIONS_IDS.IMPORTANT_ELEMENTS);
    const interests = getAnswersValue(QUESTIONS_IDS.INTERESTS);
    const firstWeightPredictDate = getAnswersValue(QUESTIONS_IDS.FIRST_WEIGHT_PRREDICT_DATE);

    useEffect(() => {
        if (currentComponent === COMPONENTS.AI_PAYWALL) {
            enableExperiment({
                experimentId: ExperimentIds.WEEK_TRIAL,
                enableCondition: isCurrencyLoaded,
            });
        }
        if (currentComponent === COMPONENTS.EMAIL_SUBSCRIBE) {
            enableExperiment({
                experimentId: ExperimentIds.NEW_PAYMENT_VERSION,
                attributes: { flowType },
                enableCondition: flowType !== FlowTypes.Email && flowType !== FlowTypes.Friends,
            });
        }

        // eslint-disable-next-line
    }, [currentComponent]);

    useEffect(() => {
        if (initComponent === COMPONENTS.AUTH_FORM) {
            enableExperiment({
                experimentId: ExperimentIds.NEW_PAYMENT_VERSION,
                attributes: { flowType },
                enableCondition: flowType !== FlowTypes.Email && flowType !== FlowTypes.Friends,
            });
        }
        if (initComponent === COMPONENTS.AI_PAYWALL) {
            enableExperiment({
                experimentId: ExperimentIds.NEW_PAYMENT_VERSION,
                attributes: { flowType },
                enableCondition: flowType !== FlowTypes.Email && flowType !== FlowTypes.Friends,
            });
        }
        if (initComponent === COMPONENTS.QUESTIONS) {
            enableExperiment({
                experimentId: ExperimentIds.NUMBER_OF_STEPS,
            });
        }
        // eslint-disable-next-line
    }, [initComponent]);

    useEffect(() => {
        if (currentComponent === COMPONENTS.QUESTIONS && strapiExperimentsIds?.length) {
            strapiExperimentsIds.forEach((experimentId) => {
                enableExperiment({
                    experimentId,
                });
            });
        }
        // eslint-disable-next-line
    }, [initComponent, strapiExperimentsIds]);

    useEffect(() => {
        if (initComponent === COMPONENTS.AI_PAYWALL) {
            enableExperiment({
                experimentId: ExperimentIds.WEEK_TRIAL,
                enableCondition: isCurrencyLoaded,
            });
        }
        // eslint-disable-next-line
    }, [initComponent, isCurrencyLoaded]);

    useEffect(() => {
        const femaleEnableExpQuestion = isLoseWeightGoal ? !!firstWeightPredictDate : !!interests;
        const isOnEnableExpQuestion = isFemale ? femaleEnableExpQuestion : !!importantElements;

        if (initComponent === COMPONENTS.QUESTIONS && isOnEnableExpQuestion) {
            enableExperiment({
                experimentId: ExperimentIds.COMPARE_USERS_RESPONSES,
                attributes: { flowType },
            });
        }
        // eslint-disable-next-line
    }, [initComponent, importantElements, firstWeightPredictDate, interests]);

    useEffect(() => {
        if (
            currentComponent === COMPONENTS.QUESTIONS && isFemale
                ? eventMotivation
                : supportBoostsMotivation
        ) {
            enableExperiment({
                experimentId: ExperimentIds.USER_SUMMARY,
            });
        }
        // eslint-disable-next-line
    }, [currentComponent, eventMotivation, supportBoostsMotivation]);

    useEffect(() => {
        const prepaywallComponent =
            isFemale && isLoseWeightGoal ? COMPONENTS.AI_WEIGHT_PREDICT : COMPONENTS.CHART;
        const canBeEnabledOnPaywall = initComponent === COMPONENTS.AI_PAYWALL && isCurrencyLoaded;

        if (currentComponent === prepaywallComponent || canBeEnabledOnPaywall) {
            enableExperiment({
                experimentId: ExperimentIds.COMPARE_ZING_VS_COACH_ITER1,
            });

            if (isApplePayAvailable) {
                enableExperiment({
                    experimentId: ExperimentIds.LIFT_APPLE_PAY_ON_PAYWALL,
                });
            }
        }
        // eslint-disable-next-line
    }, [
        currentComponent,
        initComponent,
        isLoseWeightGoal,
        isCurrencyLoaded,
        isAndroid,
        isApplePayAvailable,
    ]);

    useEffect(() => {
        if (waterConsumption) {
            enableExperiment({
                experimentId: ExperimentIds.DYNAMIC_PRICES,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFemale, waterConsumption, flowType]);

    useEffect(() => {
        if (
            (currentComponent === COMPONENTS.AUTH_FORM || initComponent === COMPONENTS.AUTH_FORM) &&
            isCurrencyLoaded
        ) {
            enableExperiment({
                experimentId: ExperimentIds.AI_LAB_UPSELL,
            });
            enableExperiment({
                experimentId: ExperimentIds.NUTRITION_BOOK_ITER2,
            });

            if (!isAndroid) {
                enableExperiment({
                    experimentId: ExperimentIds.AI_LAB_UPSELL,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentComponent, initComponent, isCurrencyLoaded]);
};
